"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

const CheckIcon = () => (
	<svg className="h-6 w-6 flex-shrink-0 text-themePrimary400" viewBox="0 0 20 20" fill="currentColor">
		<path
			fillRule="evenodd"
			d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
			clipRule="evenodd"
		/>
	</svg>
);

export const DEFAULT_ADVANTAGES = [
	{ titleKey: "advantage1Title", descKey: "advantage1Desc" },
	{ titleKey: "advantage2Title", descKey: "advantage2Desc" },
	{ titleKey: "advantage3Title", descKey: "advantage3Desc" },
	{ titleKey: "advantage4Title", descKey: "advantage4Desc" },
	{ titleKey: "advantage5Title", descKey: "advantage5Desc" },
	{ titleKey: "advantage6Title", descKey: "advantage6Desc" },
] as const;

export const SEVEN_ADVANTAGES = [
	{ titleKey: "advantage1Title", descKey: "advantage1Desc" },
	{ titleKey: "advantage2Title", descKey: "advantage2Desc" },
	{ titleKey: "advantage3Title", descKey: "advantage3Desc" },
	{ titleKey: "advantage4Title", descKey: "advantage4Desc" },
	{ titleKey: "advantage5Title", descKey: "advantage5Desc" },
	{ titleKey: "advantage6Title", descKey: "advantage6Desc" },
	{ titleKey: "advantage7Title", descKey: "advantage7Desc" },
] as const;

const MILLING_IMAGE_SRC = "/image/new-version/machining/cnc-milling/key-advantages-of-cnc-milling.webp";

export interface AdvantageProps {
	translationNamespace?: string;
	imageSrc?: string;
	advantages?: readonly { titleKey: string; descKey: string }[];
	layout?: "image-left" | "header-top";
}

export default function Advantage({
	translationNamespace = "cncMillingAdvantages",
	imageSrc = MILLING_IMAGE_SRC,
	advantages = SEVEN_ADVANTAGES,
	layout = "header-top",
}: AdvantageProps) {
	const t = useTranslations(translationNamespace);

	if (layout === "image-left") {
		return (
			<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
				<div className="container">
					<div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-12 xl:gap-16">
						<div className="w-full lg:w-[40%]">
							<div className="relative w-full overflow-hidden">
								<Image
									src={imageSrc}
									alt={t("imageAlt")}
									width={1000}
									height={1000}
									className="h-auto w-full"
									sizes="(max-width: 1024px) 100vw, 40vw"
								/>
							</div>
						</div>

						<div className="w-full lg:w-[60%]">
							<span className="mb-3 inline-block text-xl uppercase tracking-wider text-themePrimary400 max-md:text-lg">
								{t("subtitle")}
							</span>

							<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
								{t("title")}
							</h2>

							<p className="mb-8 text-sm leading-relaxed text-black md:text-base">{t("description")}</p>

							<div className="space-y-4">
								{advantages.map((advantage, index) => (
									<div key={index} className="flex items-start gap-x-6 gap-y-3">
										<span className="mt-1">
											<CheckIcon />
										</span>
										<p className="text-sm leading-relaxed text-black md:text-base">
											<span className="font-bold">{t(advantage.titleKey)}</span> {t(advantage.descKey)}
										</p>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</section>
		);
	}

	return (
		<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12">
					<span className="mb-3 inline-block text-xl uppercase tracking-wider text-themePrimary400 max-md:text-lg">
						{t("subtitle")}
					</span>

					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>

					<p className="text-sm leading-relaxed text-black md:text-base">{t("description")}</p>
				</div>

				<div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-12 xl:gap-16">
					<div className="w-full lg:w-[40%]">
						<div className="relative w-full overflow-hidden lg:sticky lg:top-24">
							<Image
								src={imageSrc}
								alt={t("imageAlt")}
								width={1000}
								height={1000}
								className="h-auto w-full"
								sizes="(max-width: 1024px) 100vw, 40vw"
							/>
						</div>
					</div>

					<div className="w-full lg:w-[60%]">
						<div className="space-y-5">
							{advantages.map((advantage, index) => (
								<div key={index} className="flex items-start gap-x-4">
									<span className="mt-0.5">
										<CheckIcon />
									</span>
									<div>
										<h3 className="mb-1 text-base font-semibold text-black sm:text-lg">
											{t(advantage.titleKey)}
										</h3>
										<p className="text-sm leading-relaxed text-black md:text-base">{t(advantage.descKey)}</p>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
